import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useLocalization } from "../../../hooks/useLocalization";
import { USERS_FOR_RELEASE_CHANNEL_QUERY_KEY } from "../../experiences/hooks/useUsersForReleaseChannel";
import { useUsers } from "../../users/hooks/useUsers";
import { TAccessSource, TExperienceUserAccessRow } from "../types";
import { runWithConcurrency } from "../utils";

export const USER_GROUPS_BY_IDS_QUERY_KEY = "userGroupsByIds";

type UserAccessData = UserManagement_V1.UserAccessData;
type UserWithCompanyName = UserManagement_V1.UserWithCompanyName;

const getSourceName = (
  isDirect: boolean,
  userGroupId: string | null,
  groupNames: Map<string, string>,
  t: (key: string) => string,
  deletedGroupIds: Set<string>,
): string => {
  if (isDirect) {
    return t("experiences.directAccess");
  }

  if (userGroupId) {
    if (deletedGroupIds.has(userGroupId)) {
      return "Deleted group";
    }

    return groupNames.get(userGroupId) ?? userGroupId;
  }

  return t("experiences.groupAccess");
};

const getSourceId = (
  isDirect: boolean,
  userId: string,
  userGroupId: string | null,
): string => {
  return isDirect ? `direct-${userId}` : (userGroupId ?? "");
};

const updateLongestExpiry = (
  current: string | null,
  next: string | null,
): string | null => {
  // If next is unlimited, return unlimited
  if (next === null) {
    return null;
  }

  // If current is unlimited, keep unlimited
  if (current === null) {
    return next;
  }

  return next > current ? next : current;
};

const createAccessSource = (
  isDirect: boolean,
  userId: string,
  userGroupId: string | null,
  expiresAtStr: string | null,
  groupNames: Map<string, string>,
  t: (key: string) => string,
  deletedGroupIds: Set<string>,
): TAccessSource => {
  return {
    id: getSourceId(isDirect, userId, userGroupId),
    type: isDirect ? "DIRECT" : "GROUP",
    name: getSourceName(isDirect, userGroupId, groupNames, t, deletedGroupIds),
    expiresAt: expiresAtStr,
  };
};

const createUserRow = (
  userId: string,
  fullName: string,
  email: string,
): TExperienceUserAccessRow => {
  return {
    userId,
    fullName,
    email,
    sources: [],
    longestExpiry: null,
    hasDirectAccess: false,
    hasGroupAccess: false,
  };
};

const getUserDisplayName = (
  user: UserWithCompanyName | undefined,
  t: (key: string) => string,
): string => {
  return user
    ? `${user.surname}, ${user.forename}`
    : t("experiences.unknownUser");
};

const mapToRows = (
  items: UserAccessData[],
  users: UserWithCompanyName[],
  groupNames: Map<string, string>,
  t: (key: string) => string,
  deletedGroupIds: Set<string>,
): TExperienceUserAccessRow[] => {
  const byUser = new Map<string, TExperienceUserAccessRow>();

  const userMap = new Map<string, UserWithCompanyName>();
  for (const u of users) {
    userMap.set(u.userId, u);
  }

  for (const entry of items) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const userId: string = (entry as any).userId ?? "";
    if (!userId) continue;

    const user = userMap.get(userId);
    const fullName = getUserDisplayName(user, t);
    const email = user?.emailAddress ?? "";

    let existing = byUser.get(userId);
    if (!existing) {
      existing = createUserRow(userId, fullName, email);
      byUser.set(userId, existing);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const userGroupId: string | null = (entry as any).userGroupId ?? null;
    const isDirect = !userGroupId;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const expiresAtStr: string | null = (entry as any).expiresAt
      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        (entry as any).expiresAt.toISOString()
      : null;

    const source = createAccessSource(
      isDirect,
      userId,
      userGroupId,
      expiresAtStr,
      groupNames,
      t,
      deletedGroupIds,
    );

    existing.sources.push(source);
    existing.longestExpiry = updateLongestExpiry(
      existing.longestExpiry,
      source.expiresAt,
    );

    if (isDirect) {
      existing.hasDirectAccess = true;
    } else {
      existing.hasGroupAccess = true;
    }
  }

  return Array.from(byUser.values());
};

interface UseExperienceUsersParams {
  releaseChannelId?: string;
  companyId?: string;
  enabled?: boolean;
}

export const useExperienceUsers = ({
  releaseChannelId,
  companyId,
  enabled = true,
}: UseExperienceUsersParams): {
  loading: boolean;
  rows: TExperienceUserAccessRow[];
} => {
  const { t } = useLocalization();
  const userManagement = useUserManagementServiceV1();

  const { data: accessData, isLoading: loadingAccess } = useQuery<
    UserAccessData[]
  >({
    queryKey: [USERS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId],
    enabled: !!releaseChannelId && enabled,
    staleTime: 1000 * 60 * 5,
    queryFn: async (): Promise<UserAccessData[]> => {
      if (!releaseChannelId) return [];

      const allResults: UserAccessData[] = [];
      let pageNumber = 0;
      const pageSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const results = await userManagement.getUsersForReleaseChannel(
          releaseChannelId,
          pageNumber,
          pageSize,
        );

        if (Array.isArray(results)) {
          allResults.push(...results);

          if (results.length < pageSize) {
            hasMore = false;
          } else {
            pageNumber++;
          }
        } else {
          hasMore = false;
        }
      }

      return allResults;
    },
  });

  const { users: allCompanyUsers, isLoading: loadingCompanyUsers } = useUsers({
    companyId,
    enabled: !!companyId && enabled,
  });

  // Extract Unique IDs
  const { uniqueUserIds, uniqueGroupIds } = useMemo(() => {
    const uIds = new Set<string>();
    const gIds = new Set<string>();
    accessData?.forEach((entry) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const userId: string | null = (entry as any).userId ?? null;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const groupId: string | null = (entry as any).userGroupId ?? null;

      if (userId) uIds.add(userId);
      if (groupId) gIds.add(groupId);
    });

    return {
      uniqueUserIds: Array.from(uIds),
      uniqueGroupIds: Array.from(gIds),
    };
  }, [accessData]);

  // Filter to only the users we need
  const validUsers = useMemo(() => {
    if (!allCompanyUsers.length) return [];

    const userIdSet = new Set(uniqueUserIds);

    return allCompanyUsers.filter((u) => userIdSet.has(u.userId));
  }, [allCompanyUsers, uniqueUserIds]);

  // Fetch ONLY the required Groups
  const { data: groupsData, isLoading: loadingGroups } = useQuery({
    queryKey: [USER_GROUPS_BY_IDS_QUERY_KEY, uniqueGroupIds],
    enabled: uniqueGroupIds.length > 0 && enabled,
    staleTime: 1000 * 60 * 10,
    queryFn: () =>
      runWithConcurrency(
        uniqueGroupIds,
        (id) => userManagement.getUserGroupById(id).catch(() => null),
        20,
      ),
  });

  const { groupNames, deletedGroupIds } = useMemo(() => {
    const map = new Map<string, string>();
    const deleted = new Set<string>();

    const requestedIds = new Set(uniqueGroupIds);

    groupsData?.forEach((group, index) => {
      const requestedId = uniqueGroupIds[index];
      if (group) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        map.set((group as any).userGroupId, (group as any).userGroupName);
      } else if (requestedId) {
        deleted.add(requestedId);
      }
    });

    if (groupsData) {
      for (const id of requestedIds) {
        if (!map.has(id) && !deleted.has(id)) {
          deleted.add(id);
        }
      }
    }

    return { groupNames: map, deletedGroupIds: deleted };
  }, [groupsData, uniqueGroupIds]);

  const rows = useMemo(() => {
    // Show data as it loads
    // If users data isn't ready yet, show userId as placeholder
    return mapToRows(
      accessData ?? [],
      validUsers,
      groupNames,
      t,
      deletedGroupIds,
    );
  }, [accessData, validUsers, groupNames, t, deletedGroupIds]);

  return {
    loading: loadingAccess || loadingCompanyUsers || loadingGroups,
    rows,
  };
};
